'use client';

import { css } from '@onbeam/business-styled-system/css';
import { useEffect, useRef } from 'react';
import { motion } from 'motion/react';
import { fragmentShaderSource, vertexShaderSource } from './HeroShader.data';
import {
  initShaderProgram,
  loadImage,
  cleanupWebGLResources,
  resizeCanvas,
  setupBuffers,
  setUniforms,
  bindTexture,
  getUniformLocations,
} from './HeroShader.utils';
import { ProgramInfo } from './HeroShader.types';

export const HERO_IMG_SRC = '/images/hero-shader.png';

export const HeroShader = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const gl = canvas.getContext('webgl');

    if (!gl) {
      console.error('WebGL not supported');
      return;
    }

    let shaderProgram: WebGLProgram | null = null;
    let texture: WebGLTexture | null = null;
    let positionBuffer: WebGLBuffer | null = null;
    let textureCoordBuffer: WebGLBuffer | null = null;
    let programInfo: ProgramInfo | null = null;

    const startTime = Date.now();

    const main = async () => {
      const image = await loadImage(HERO_IMG_SRC);

      shaderProgram = initShaderProgram(
        gl,
        vertexShaderSource,
        fragmentShaderSource,
      );

      if (!shaderProgram) return;

      programInfo = {
        program: shaderProgram,
        attribLocations: {
          vertexPosition: gl.getAttribLocation(
            shaderProgram,
            'aVertexPosition',
          ),
          textureCoord: gl.getAttribLocation(shaderProgram, 'aTextureCoord'),
        },
        uniformLocations: getUniformLocations(gl, shaderProgram),
      };

      const buffers = setupBuffers(gl);
      positionBuffer = buffers.positionBuffer;
      textureCoordBuffer = buffers.textureCoordBuffer;

      texture = bindTexture(gl, image);

      const resize = () => resizeCanvas(canvas, gl);
      window.addEventListener('resize', resize);
      resize();

      const render = () => {
        if (!programInfo) {
          console.error('Failed to get program info');
          return;
        }

        gl.clearColor(0, 0, 0, 1);
        gl.clear(gl.COLOR_BUFFER_BIT);

        gl.useProgram(programInfo.program);

        setUniforms(gl, programInfo.uniformLocations, canvas, startTime);

        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, texture);
        gl.uniform1i(programInfo.uniformLocations.image, 0);

        gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
        gl.vertexAttribPointer(
          programInfo.attribLocations.vertexPosition,
          2,
          gl.FLOAT,
          false,
          0,
          0,
        );
        gl.enableVertexAttribArray(programInfo.attribLocations.vertexPosition);

        gl.bindBuffer(gl.ARRAY_BUFFER, textureCoordBuffer);
        gl.vertexAttribPointer(
          programInfo.attribLocations.textureCoord,
          2,
          gl.FLOAT,
          false,
          0,
          0,
        );
        gl.enableVertexAttribArray(programInfo.attribLocations.textureCoord);

        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

        animationRef.current = requestAnimationFrame(render);
      };

      animationRef.current = requestAnimationFrame(render);
    };

    main();

    return () => {
      window.removeEventListener('resize', () => resizeCanvas(canvas, gl));
      if (!gl) return;
      cleanupWebGLResources({
        gl,
        canvas,
        animationRef,
        texture,
        positionBuffer,
        textureCoordBuffer,
        shaderProgram,
      });
    };
  }, []);

  return (
    <motion.canvas
      ref={canvasRef}
      className={css({
        bg: 'mono.quartenery',
        pos: 'absolute',
        w: 'full',
        h: 'full',
      })}
      initial={{ opacity: 1, zIndex: -2 }}
      animate={{ opacity: 1, zIndex: -2 }}
      exit={{ opacity: 0, zIndex: -1, transition: { delay: 0.15 } }}
      transition={{ duration: 1 }}
    />
  );
};
